import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { displayScreenClient, type PairingCode } from "./displaysClient";
import { displayScreenQuery } from "./displaysQueries";

const RESTART_AFTER_FAILURE_MS = 15_000;

type DisplayPairingState =
  | { status: "starting" }
  | { status: "unreachable" }
  | {
      status: "showing";
      userCode: string;
      expiresAt: number;
    };

const sleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const id = setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(id);
        reject(signal.reason);
      },
      { once: true },
    );
  });

/** True once approved and collected; false when the code lapsed first. */
const awaitApproval = async (
  code: PairingCode,
  expiresAt: number,
  signal: AbortSignal,
): Promise<boolean> => {
  for (;;) {
    await sleep(code.intervalSeconds * 1000, signal);
    if (Date.now() >= expiresAt) return false;
    try {
      const poll = await displayScreenClient.pollPairing(
        code.pollingSecret,
        signal,
      );
      if (poll.status !== "pending") return poll.status === "paired";
    } catch (err) {
      if (signal.aborted) throw err;
    }
  }
};

/**
 * Shows a code and waits for a staff computer to approve it, then a fresh one.
 * The polling secret never leaves this effect: no state, storage, URL or log.
 */
export const useDisplayPairing = () => {
  const queryClient = useQueryClient();
  const [state, setState] = useState<DisplayPairingState>({
    status: "starting",
  });
  const [cookieRefused, setCookieRefused] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const run = async () => {
      for (;;) {
        let code: PairingCode;
        try {
          code = await displayScreenClient.startPairing(signal);
        } catch (err) {
          if (signal.aborted) throw err;
          setState({ status: "unreachable" });
          await sleep(RESTART_AFTER_FAILURE_MS, signal);
          continue;
        }
        const expiresAt = Date.now() + code.expiresInSeconds * 1000;
        setState({
          status: "showing",
          userCode: code.userCode,
          expiresAt,
        });
        if (!(await awaitApproval(code, expiresAt, signal))) continue;
        // Paired screens leave this hook through the query; null means the browser dropped the cookie.
        if (await queryClient.fetchQuery(displayScreenQuery)) return;
        setCookieRefused(true);
      }
    };

    run().catch(() => {});
    return () => controller.abort();
  }, [queryClient]);

  return { state, cookieRefused };
};
